/**
 * SimulationActionBar - Sticky card-based header for simulation controls.
 *
 * Consolidates Run Simulation, Import/Export, and settings into a cohesive
 * card that pins below the app header on scroll. Features an inline-editable
 * name (full width) and a click-to-edit markdown description.
 */

import { useId, useState, useRef } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import {
  Play,
  Download,
  Upload,
  ChevronDown,
  Check,
  Loader2,
  AlertTriangle,
  BarChart2,
  Calendar,
  Repeat,
  Hash,
  FileText,
  Lock,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format, parseISO } from 'date-fns';
import { cn } from '@lean-agility/design-system/react';
import { Button } from '@lean-agility/design-system/react';
import { Input } from '@lean-agility/design-system/react';
import { DatePicker } from '@lean-agility/design-system/react';
import { Card, CardHeader, CardContent } from '@lean-agility/design-system/react';
import { Spinner } from '@lean-agility/design-system/react';
import { Label, IconContainer, MenuItem } from '@/components/atoms';
import { Dropdown, Menu, Collapsible } from '@/components/molecules';
import { exportSimulationSheet, importSimulationSheet, downloadBlob } from '@/lib/api';
import { useInvalidateSimulation } from '@/hooks/queries';
import { useUpdateSimulation } from '@/hooks/queries/useSimulationQueries';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useErrorHandler, useSimulationSettingsAutoSave, useSessionState } from '@/hooks';
import { useWriteErrors } from '@/hooks/useWriteErrors';
import { MAX_SIMULATION_RUNS } from '@/lib/validation';
import type { SimulationFormValues, UpdateSimulationRequest } from '@/types/api';

export interface SimulationActionBarProps {
  /** Simulation ID for API calls */
  simulationId: string;
  /** Whether the form is currently submitting (POST in flight) */
  isSubmitting: boolean;
  /** Whether a background simulation run is in progress */
  isSimulationRunning?: boolean;
  /** Callback to refresh form data after import */
  onRefresh?: () => Promise<void>;
  /** Whether the Run button should be disabled due to license issues */
  licenseBlocked?: boolean;
  /** Message to display when license-blocked (role-aware) */
  licenseMessage?: React.ReactNode;
}

/** Save status indicator - minimal icon-only display */
function SaveStatusIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'saving') {
    return <Loader2 className="size-4 animate-spin text-action-primary" />;
  }
  if (status === 'saved') {
    return <Check className="size-4 text-feedback-success" />;
  }
  if (status === 'error') {
    return (
      <span title="Save failed">
        <AlertTriangle className="size-4 text-feedback-error" />
      </span>
    );
  }
  return null;
}

/**
 * SimulationActionBar - Sticky top action bar with run, import/export controls.
 *
 * Must be used within a react-hook-form FormProvider context.
 */
export function SimulationActionBar({
  simulationId,
  isSubmitting,
  isSimulationRunning,
  onRefresh,
  licenseBlocked,
  licenseMessage,
}: SimulationActionBarProps) {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<SimulationFormValues>();

  // Ties each settings label to its input, so the field has an accessible name.
  const runsId = useId();
  const seedId = useId();
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useSessionState(`desc-expanded-${simulationId}`, false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useSessionState(`settings-expanded-${simulationId}`, false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { handleError, handleSuccess } = useErrorHandler('Import/Export');
  const { handleError: handleSaveError } = useErrorHandler('Simulation');
  const { onSaveError } = useWriteErrors('simulation', handleSaveError);
  const invalidateSimulation = useInvalidateSimulation();

  // Auto-save start_date and seed when changed
  const { saveStatus: settingsSaveStatus } = useSimulationSettingsAutoSave({ simulationId, onError: onSaveError });

  // Auto-save name and description
  const name = useWatch({ control, name: 'name' });
  const description = useWatch({ control, name: 'description' });
  const startDate = useWatch({ control, name: 'start_date' });
  const numSimulations = useWatch({ control, name: 'num_simulations' });
  const seed = useWatch({ control, name: 'seed' });
  const updateMutation = useUpdateSimulation(simulationId);

  const { status: identitySaveStatus, saveNow: saveIdentityNow } = useAutoSave({
    values: { name, description },
    fields: [
      { name: 'name', skipIf: (v) => !(v as string)?.trim() },
      { name: 'description', transform: (v) => (v as string)?.trim() || null },
    ],
    saveFn: (payload) => updateMutation.mutateAsync(payload as UpdateSimulationRequest),
    delay: 1500,
    enabled: !!simulationId,
    onError: onSaveError,
  });

  // Combine save statuses: show whichever is most "active"
  const combinedSaveStatus =
    identitySaveStatus === 'saving' || settingsSaveStatus === 'saving'
      ? 'saving'
      : identitySaveStatus === 'error' || settingsSaveStatus === 'error'
        ? 'error'
        : identitySaveStatus === 'saved' || settingsSaveStatus === 'saved'
          ? 'saved'
          : 'idle';

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportSimulationSheet(simulationId);
      const name = getValues('name') || 'simulation';
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(/:/g, '-');
      downloadBlob(blob, `${name} ${timestamp}.xlsx`);
      handleSuccess('Simulation exported successfully');
    } catch (err) {
      handleError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      await importSimulationSheet(simulationId, file);
      if (onRefresh) {
        await onRefresh();
      } else {
        invalidateSimulation(simulationId);
      }
      handleSuccess('Successfully imported simulation data');
    } catch (err) {
      handleError(err);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div
      className="
        mb-6
        md:sticky md:z-40
      "
      style={{ top: 'var(--header-h, 0px)' }}
    >
      <Card elevation="soft" className="md:bg-bg-elevated/95! md:shadow-lg md:backdrop-blur-sm">
        <CardHeader>
          <div
            className="
              grid grid-cols-1 items-start gap-3
              md:grid-cols-[minmax(0,1fr)_auto] md:gap-4
            "
          >
            {/* Left: Identity zone */}
            <div className="flex min-w-0 items-start gap-3">
              <IconContainer variant="accent" size="md" className="mt-0.5 shrink-0">
                <BarChart2 className="size-5 text-text-accent" />
              </IconContainer>
              <div className="min-w-0 flex-1">
                {/* Name input - full width */}
                <input
                  type="text"
                  className="
                    -mx-1 w-full rounded-sm border border-transparent bg-transparent px-1 text-xl font-semibold
                    text-text-primary transition-colors
                    placeholder:text-text-muted
                    hover:border-border-strong
                    focus:border-transparent focus:ring-1 focus:ring-primary-500 focus:outline-hidden
                  "
                  placeholder="Untitled Simulation"
                  {...register('name', { onBlur: () => void saveIdentityNow() })}
                />

                {/* Description toggle - collapsed preview or expand to full editor */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !isDescriptionExpanded;
                    setIsDescriptionExpanded(next);
                    if (next) setIsEditingDescription(false);
                  }}
                  className="
                    group mt-1 flex min-w-0 items-center gap-1.5 text-sm text-text-muted transition-colors
                    hover:text-text-secondary
                  "
                >
                  <ChevronDown
                    className={cn(
                      'size-3.5 shrink-0 transition-transform duration-200',
                      isDescriptionExpanded && 'rotate-180',
                    )}
                  />
                  <FileText className="size-3.5 shrink-0" />
                  {!isDescriptionExpanded && (
                    <span
                      className="
                        prose prose-sm min-w-0 truncate
                        *:m-0 *:inline
                      "
                    >
                      {description ? (
                        <ReactMarkdown>{description.split('\n')[0]}</ReactMarkdown>
                      ) : (
                        <span className="italic">Add description...</span>
                      )}
                    </span>
                  )}
                  {isDescriptionExpanded && <span>Description</span>}
                </button>

                {/* Expanded description area */}
                {isDescriptionExpanded && (
                  <div className="mt-2">
                    {isEditingDescription ? (
                      <textarea
                        className="
                          max-h-64 min-h-32 w-full resize-y rounded-sm border border-border-strong bg-transparent p-2
                          text-sm text-text-secondary transition-colors
                          placeholder:text-text-muted
                          focus:border-transparent focus:ring-1 focus:ring-primary-500 focus:outline-hidden
                        "
                        placeholder="Describe this simulation... (markdown supported)"
                        autoFocus
                        value={description}
                        onChange={(e) => setValue('description', e.target.value)}
                        onBlur={() => {
                          setIsEditingDescription(false);
                          void saveIdentityNow();
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => setIsEditingDescription(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsEditingDescription(true);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Click to edit description"
                        className="
                          prose prose-sm max-h-64 min-h-16 max-w-none cursor-text overflow-y-auto rounded-sm border
                          border-transparent p-2 transition-colors
                          hover:border-border-strong
                        "
                      >
                        {description ? (
                          <ReactMarkdown>{description}</ReactMarkdown>
                        ) : (
                          <p className="my-0! text-text-muted italic">
                            Click to add a description... (markdown supported)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Auto-save indicator */}
              <div className="mt-2 shrink-0">
                <SaveStatusIndicator status={combinedSaveStatus} />
              </div>
            </div>

            {/* Right: Actions zone */}
            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2">
                {/* Hidden file input for import */}
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx" className="hidden" />

                {/* Import/Export Dropdown */}
                <Dropdown
                  trigger={
                    <Button type="button" variant="secondary" size="sm" data-testid="excel-dropdown">
                      {isImporting || isExporting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="mr-1 size-4" />
                          <span
                            className="
                              hidden
                              sm:inline
                            "
                          >
                            Excel
                          </span>
                          <ChevronDown className="ml-1 size-3" />
                        </>
                      )}
                    </Button>
                  }
                >
                  <Menu width="sm">
                    <MenuItem icon={Upload} onClick={handleImportClick} disabled={isImporting}>
                      Import from Excel
                    </MenuItem>
                    <MenuItem icon={Download} onClick={handleExport} disabled={isExporting}>
                      Export to Excel
                    </MenuItem>
                  </Menu>
                </Dropdown>

                {/* Run Simulation - Primary Action */}
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={isSubmitting || isSimulationRunning || licenseBlocked}
                  title={licenseBlocked ? 'A valid license is required to run simulations' : undefined}
                  className="min-w-40"
                  data-testid="run-simulation-button"
                >
                  {isSimulationRunning ? (
                    <>
                      <Spinner size="sm" className="text-white" />
                      <span className="ml-1">Running...</span>
                    </>
                  ) : isSubmitting ? (
                    <>
                      <Spinner size="sm" className="text-white" />
                      <span className="ml-1">Starting...</span>
                    </>
                  ) : (
                    <>
                      <Play className="mr-1 size-4" />
                      Run Simulation
                    </>
                  )}
                </Button>
              </div>

              {/* License warning — anchored below the Run button */}
              {licenseBlocked && licenseMessage && (
                <div className="flex items-center gap-1.5 text-xs text-feedback-warning" data-testid="license-warning">
                  <Lock className="size-3.5 shrink-0" />
                  <span>{licenseMessage}</span>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Settings Zone - collapsible */}
        <Collapsible
          header={<span className="text-sm font-medium text-text-secondary">Simulation Settings</span>}
          summary={
            <span
              className="
                flex flex-wrap items-center gap-2 text-sm text-text-muted
                md:gap-3
              "
            >
              <span className="flex items-center gap-1">
                <Calendar className="size-3.5" />
                {startDate ? format(parseISO(startDate), 'MMM d, yyyy') : 'Today'}
              </span>
              <span className="text-text-disabled">·</span>
              <span className="flex items-center gap-1">
                <Repeat className="size-3.5" />
                {numSimulations || 1000} runs
              </span>
              <span className="text-text-disabled">·</span>
              <span className="flex items-center gap-1">
                <Hash className="size-3.5" />
                {seed || 'Random'}
              </span>
            </span>
          }
          isExpanded={isSettingsExpanded}
          onExpandedChange={setIsSettingsExpanded}
          className="
            px-6 pb-2
            md:pl-19
          "
          contentClassName="pt-0"
          data-testid="simulation-settings"
        >
          <CardContent variant="flush">
            <div className="rounded-xl border border-border-default/20 bg-bg-muted/30 p-5">
              <div
                className="
                  flex flex-wrap justify-center gap-6
                  lg:gap-10
                "
              >
                {/* Start Date */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="size-4 text-text-secondary" />
                    <Label>Start Date</Label>
                  </div>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        data-testid="start-date-picker"
                        className="w-40 px-3 py-2 text-sm"
                        placeholder="Today (default)"
                        error={errors.start_date?.message}
                        clearable
                      />
                    )}
                  />
                  <p className="text-xs text-text-muted">Simulation start</p>
                </div>

                {/* Simulations count */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Repeat className="size-4 text-text-secondary" />
                    <Label htmlFor={runsId}>Runs</Label>
                  </div>
                  <Input
                    id={runsId}
                    type="number"
                    min={1}
                    max={MAX_SIMULATION_RUNS}
                    placeholder="1000"
                    className="w-30"
                    data-testid="runs-input"
                    error={errors.num_simulations?.message}
                    {...register('num_simulations')}
                  />
                  <p className="text-xs text-text-muted">More = higher accuracy</p>
                </div>

                {/* Seed */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Hash className="size-4 text-text-secondary" />
                    <Label htmlFor={seedId}>Seed</Label>
                  </div>
                  <Input
                    id={seedId}
                    type="number"
                    placeholder="Random"
                    className="w-30"
                    data-testid="seed-input"
                    error={errors.seed?.message}
                    {...register('seed')}
                  />
                  <p className="text-xs text-text-muted">For reproducibility</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Collapsible>
      </Card>
    </div>
  );
}
